/**
 * OutcomeWidget — compact outcome editor for a single analysis.
 *
 * Self-loads its current state from /api/outcomes/by-analysis/:id.
 * Renders one of:
 *   - A clickable chip showing the current outcome (status + stage)
 *   - A "Did you hear back?" CTA when no outcome is logged
 *   - An inline form (when expanded) to log/update outcome
 *
 * Props:
 *   - analysisId   string  required
 *   - compact?     bool    if true, renders chip-only view (used in history cards)
 *   - onChanged?   fn      called after a successful save
 */
import React, { useEffect, useState } from "react";
import { Check, ChevronDown, ChevronUp, Heart, Loader, MessageSquare, Mic, Send, Trophy, XCircle, Briefcase } from "lucide-react";
import { api } from "../lib/api";

export const STATUS_META = {
  applied:           { label: "Applied",   icon: Send,        color: "#B45309", soft: "#FEF3C7" },
  interview_request: { label: "Interview", icon: Mic,         color: "#1E40AF", soft: "#DBEAFE" },
  offer:             { label: "Offer",     icon: Trophy,      color: "#0E5A4A", soft: "#CFFAFE" },
  hired:             { label: "Hired",     icon: Briefcase,   color: "#047857", soft: "#D1FAE5" },
  no_response:       { label: "Silence",   icon: MessageSquare, color: "#64748B", soft: "#F1F5F9" },
  rejection:         { label: "Rejected",  icon: XCircle,     color: "#B91C1C", soft: "#FEF2F2" },
};

const STATUSES = ["applied", "interview_request", "offer", "hired", "no_response", "rejection"];

export default function OutcomeWidget({ analysisId, compact = false, onChanged }) {
  const [outcome, setOutcome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ status: "", stage: "", salary: "", notes: "" });
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/outcomes/by-analysis/${analysisId}`);
      // data is either the outcome object OR { outcome: null }
      const hit = data && data.id ? data : null;
      setOutcome(hit);
      if (hit) {
        setForm({
          status: hit.status || "",
          stage:  hit.stage  || "",
          salary: hit.salary || "",
          notes:  hit.notes  || "",
        });
      }
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (analysisId) load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  useEffect(() => {
    const onRefresh = (e) => {
      if (!e.detail?.analysisId || e.detail.analysisId === analysisId) load();
    };
    window.addEventListener("resmo:outcome:refresh", onRefresh);
    return () => window.removeEventListener("resmo:outcome:refresh", onRefresh);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  const submit = async () => {
    if (!form.status) { setError("Pick what happened."); return; }
    setError("");
    setSaving(true);
    try {
      const { data } = await api.post("/outcomes", {
        analysis_id: analysisId,
        status:      form.status,
        stage:       form.stage  || null,
        salary:      form.salary || null,
        notes:       form.notes  || null,
      });
      setOutcome(data);
      setOpen(false);
      if (onChanged) onChanged(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save — try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
        · Loading outcome…
      </div>
    );
  }

  // Compact (history card) view — chip + click to expand inline form
  if (compact && !open) {
    if (outcome) {
      const meta = STATUS_META[outcome.status] || STATUS_META.no_response;
      const Icon = meta.icon;
      return (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 transition-transform hover:-translate-y-0.5"
          style={{
            background: meta.soft, border: `2px solid ${meta.color}`,
            borderRadius: 999, color: meta.color,
            fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
          }}
          data-testid={`outcome-chip-${analysisId}`}
          title="Click to edit outcome"
        >
          <Icon size={11} /> {meta.label}{outcome.stage ? ` · ${outcome.stage}` : ""}
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 transition-transform hover:-translate-y-0.5"
        style={{
          background: "#FFFBEB", border: "2px dashed #FFC145", borderRadius: 999, color: "#92400E",
          fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
        }}
        data-testid={`outcome-prompt-${analysisId}`}
      >
        <Heart size={11} /> Did you hear back?
      </button>
    );
  }

  // Expanded inline form
  return (
    <div className="rm-card-static p-3 mt-2"
         style={{ background: "#FFF2EC", border: "2px solid #0F1B2D" }}
         data-testid={`outcome-form-${analysisId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#B45309", fontWeight: 700 }}>
          · How did this one go?
        </div>
        <button type="button" onClick={() => setOpen(false)}
                className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                data-testid={`outcome-cancel-${analysisId}`}>
          {compact ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {STATUSES.map((s) => {
          const meta = STATUS_META[s];
          const Icon = meta.icon;
          const active = form.status === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setForm({ ...form, status: s })}
              className="inline-flex items-center gap-1 px-2 py-1 transition-transform"
              style={{
                background: active ? meta.color : "white",
                color: active ? "white" : meta.color,
                border: `2px solid ${meta.color}`,
                borderRadius: 999,
                fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
              }}
              data-testid={`outcome-status-${s}-${analysisId}`}
            >
              <Icon size={11} /> {meta.label}
            </button>
          );
        })}
      </div>

      {(form.status === "interview_request" || form.status === "offer" || form.status === "hired") && (
        <input
          className="rm-input text-xs mb-2"
          placeholder={form.status === "interview_request" ? "Stage (phone-screen, on-site, final…)" : "Stage / position"}
          value={form.stage}
          onChange={(e) => setForm({ ...form, stage: e.target.value })}
          data-testid={`outcome-stage-${analysisId}`}
        />
      )}
      {(form.status === "offer" || form.status === "hired") && (
        <input
          className="rm-input text-xs mb-2"
          placeholder="Comp / salary (optional)"
          value={form.salary}
          onChange={(e) => setForm({ ...form, salary: e.target.value })}
          data-testid={`outcome-salary-${analysisId}`}
        />
      )}
      <textarea
        className="rm-input text-xs"
        rows={2}
        placeholder="What worked? What surprised you? (optional, but it sharpens Resmo)"
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        data-testid={`outcome-notes-${analysisId}`}
      />
      {error && <div className="text-xs font-mono-rm mt-1" style={{ color: "#991B1B" }}>{error}</div>}
      <div className="flex gap-2 mt-2">
        <button onClick={submit} disabled={saving || !form.status}
                className="rm-btn rm-btn-primary text-xs px-3 py-1.5"
                data-testid={`outcome-save-${analysisId}`}>
          {saving ? <Loader size={11} className="animate-spin" /> : <Check size={11} />}
          {saving ? "Saving…" : "Save outcome"}
        </button>
      </div>
    </div>
  );
}
